import type {Agency} from 'src/api-parsers/index.types';
import type {Dispatch} from 'src/reducers';
import IndexStore from 'src/stores/index';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel';

import * as React from 'react';

import * as reduxApi from 'src/utils/redux-api-v2';
import useI18n from 'src/hooks/useI18n';

import {connect, useSelector, useDispatch} from 'react-redux';
import {Link, useHistory} from 'src/rerouter';
import flow from 'lodash/flow';
import {provideHooks} from 'src/flux/provideHooks';
import {withWindowWidth} from 'src/flux/responsive';
import {fluxify} from 'src/flux/component';
import {setAuthPath} from 'src/actions';
import {getAgency} from 'src/action-creators/agency';
import {getAllEntities} from 'src/action-creators/ats-entities';
import {
  pathFromLocation,
  stopEventImmediately,
  classify,
  addQueryParamsToUrl,
} from 'src/utils';
import {isChatAppLocation, isMobile} from 'src/utils/responsive';
import {getFromAuthedUser, hasPrivilege} from 'src/utils/accounts';
import {selectDisplayPhoneNumber, agentHasPhone} from 'src/selectors/chat';
import {selectCurrentPhoneCountry} from 'src/selectors/phone-number-sets';
import {selectFullscreen} from 'src/selectors/app-config';
import {selectUrlSuffix} from 'src/selectors/sense-ai-panel';
import {selectAgencyConfigFlag} from 'src/selectors/agency';

import useDisplayConfig from 'src/hooks/useDisplayConfig';
import {useMessagingInboxDrawer, useTRMEnabled} from 'src/hooks/product-flags';

import WalkmeButton from './walkme-button';
import {RouterErrorBoundary} from 'src/components/500/boundary';
import Loader from './loader';
import Notification from './notification';
import BadBrowserNotification from './bad-browser-notification';
import MessagesSocket from 'src/components/messaging/socket';
import ContextMenu from 'src/components/lib/context-menu';

import Logo from 'src/images/sense-logo.svg';
import PlacementsIcon from 'src/images/placements-icon.svg';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {selectTotalUnreadThreadCount} from 'src/selectors/messages';
import {selectBroadcastRecipientLimit} from 'src/selectors/broadcasts';
import {
  Panel,
  PanelBody,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {
  Modal,
  ModalBody,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import BulkMessageWrarning from 'src/components/navigation/bulk-message-warning';
import css from './top.css';
import {
  USE_MESSAGING,
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
  USE_SURVEY_SUMMARY_GENERATOR,
} from 'src/action-creators/roles';
import {LanguageSupport} from 'src/components/settings/language-support';


const peopleSecondaryNavConfig = {
  title: 'People',
  tabs: [
    {key: '/people', label: 'All People'},
    {key: '/people/leads', label: 'Leads'},
    {key: '/people/lists', label: 'Lists'},
    {key: '/people/leads/detail', label: 'Lead Detail'},
  ],
} as const;

type OuterProps = {
  children: React.ReactNode;
};

type TopNavigationProps = OuterProps & {
  store: IndexStore;
  // @ts-expect-error - TS2300 - Duplicate identifier 'location'.
  location: Location;
  authed: boolean;
  agency: Agency;
  name: string | null | undefined;
  dispatch: Dispatch;
  // @ts-expect-error - TS2300 - Duplicate identifier 'location'. | TS2717 - Subsequent property declarations must have the same type.  Property 'location' must be of type 'Location', but here has type 'any'.
  location: any;
  params: Record<any, any>;
  groupName: string | null | undefined;
  authedUserPhone: string | null | undefined;
  isMobileWindow: boolean;
  isMobile: boolean;
  context: string;
  currentPhoneCountry: string;
  audienceFilterSeparation?: boolean;
  totalUnreadCount: number;
  canViewChat: boolean;
  setIsAiPanelOpen: (value: boolean) => void;
  showAIPanel: boolean;
  hasSenseAIPermission: boolean;
  handleSenseUniversityOnClick: () => void;
};

const dependencies = {
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  required: ({dispatch}) => dispatch(getAgency()),
  // NOTE (kyle): ats entities are needed almost everywhere in our application.
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  deferred: ({dispatch}) => dispatch(getAllEntities()),
} as const;

const mapStateToProps = (state: any) => {
  return {
    agency: state.agency.agency,
    authed: state.accounts.authedUserId !== null,
    // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
    name: getFromAuthedUser(state, 'name'),
    // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
    groupName: getFromAuthedUser(state, ['securityGroup', 'name']),
    authedUserPhone: selectDisplayPhoneNumber(state),
    isMobile: isMobile(state.requestHeaders.headers['user-agent']),
    context: state.env.query.context,
    currentPhoneCountry: selectCurrentPhoneCountry(state),
    totalUnreadCount: selectTotalUnreadThreadCount(state),
    canViewChat: hasPrivilege(state, USE_MESSAGING),
    showAIPanel: state.senseAIPanel.isAiPanelOpen,
    hasSenseAIPermission:
      hasPrivilege(state, USE_JD_GENERATOR) ||
      hasPrivilege(state, USE_PRESCREENING_GENERATOR) ||
      hasPrivilege(state, USE_RESUME_GENERATOR) ||
      hasPrivilege(state, USE_SURVEY_SUMMARY_GENERATOR),
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  setIsAiPanelOpen(value: any) {
    dispatch(setAIPanelOpenEvent(value));
  },
  async handleSenseUniversityOnClick() {
    // @ts-expect-error - TS7006 - Parameter 'url' implicitly has an 'any' type.
    await dispatch(reduxApi.get('sense-university/login')).then((url) => {
      window.open(url, '_blank');
    });
  },
});

function TopNavigation(props: TopNavigationProps) {
  const {
    store,
    dispatch,
    location,
    authed,
    children,
    name,
    agency: {
      activeEventProcessor,
      brandingSettings: {logo},
      slug,
    },
    groupName,
    authedUserPhone,
    isMobile,
    isMobileWindow,
    context,
    currentPhoneCountry,
    canViewChat,
    setIsAiPanelOpen,
    showAIPanel,
    hasSenseAIPermission,
    handleSenseUniversityOnClick,
  } = props;
  const {pathname} = location;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const serverRuntimeEnvironment = useSelector(
    // @ts-expect-error - TS2339 - Property 'env' does not exist on type 'DefaultRootState'.
    (state) => state.env.serverRuntimeEnvironment,
  );
  const languageSupportEnabled = useSelector((state) =>
    selectAgencyConfigFlag(state, 'i18NEnableLanguageSupport'),
  );

  const isFullscreenExperience = useSelector(selectFullscreen);
  const urlSuffix = useSelector(selectUrlSuffix);

  const userPhoneBroadcastLimit = useSelector(selectBroadcastRecipientLimit);

  const [showMenu, setShowMenu] = React.useState(false);

  const messageRouteMatches = pathname.match(/^\/messages/);

  const totalUnreadCount: number = useSelector(selectTotalUnreadThreadCount);
  const showMessagingInboxDrawer = useMessagingInboxDrawer();
  const landingPageView = useDisplayConfig('landing_page_view');
  const trmEnabled = useTRMEnabled();

  const handleNameClick = (evt: React.SyntheticEvent) => {
    stopEventImmediately(evt);
    setShowMenu(!showMenu);
  };

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  React.useEffect(() => {
    if (showMenu) {
      const handleBodyClick = () => {
        setShowMenu(false);
      };

      document.addEventListener('click', handleBodyClick);

      return () => {
        document.removeEventListener('click', handleBodyClick);
      };
    }
  }, [showMenu]);

  React.useEffect(() => {
    setAuthPath(store, '/signin');
    const handlePostMessage = (event: any) => {
      if (event?.data?.type === 'CLOSE_AI_PANEL') {
        setIsAiPanelOpen(event.data.showAIPanel);
      }
    };
    window.addEventListener('message', handlePostMessage);
    return () => {
      // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'string'.
      setAuthPath(store, null);
      window.removeEventListener('message', handlePostMessage);
    };
  }, []);

  const fullyHidden =
    context === 'chrome-extension' ||
    context === 'pwa' ||
    context === 'trm-iframe' ||
    context === 'client-iframe' ||
    context === 'sense-ai' ||
    context === 'discover-iframe' ||
    isMobile ||
    // @ts-expect-error - TS2345 - Argument of type 'Location' is not assignable to parameter of type 'RerouterLocation'.
    (isChatAppLocation(location) && isMobileWindow) ||
    isFullscreenExperience;

  return (
    <div
      className={classify(css.container, {
        [css.topNavHidden]: fullyHidden,
      })}
    >
      <Loader />
      {/*
          Open WebSocket even if TopNavBar is fully hidden by the chrome-extension.
          TODO (gab): Eventually move socket into main app component (must first decouple
          surveys from main app)
        */}
      <MessagesSocket />
      {!fullyHidden && (
        <>
          <TopNavBar
            store={store}
            location={location}
            onNameClick={handleNameClick}
            pinned={false}
            authed={authed}
            name={name}
            logo={logo}
            showMenu={showMenu}
            groupName={groupName}
            authedUserPhone={authedUserPhone}
            currentPhoneCountry={currentPhoneCountry}
            totalUnreadCount={totalUnreadCount}
            canViewChat={canViewChat}
            // @ts-expect-error - TS2322 - Type 'RegExpMatchArray | null' is not assignable to type 'boolean'.
            messageRouteMatches={messageRouteMatches}
            showMessagingInboxDrawer={showMessagingInboxDrawer}
            setIsAiPanelOpen={setIsAiPanelOpen}
            showAIPanel={showAIPanel}
            hasSenseAIPermission={hasSenseAIPermission}
            urlSuffix={urlSuffix}
            trmEnabled={trmEnabled}
            landingPageView={landingPageView}
            userLimit={userPhoneBroadcastLimit}
            handleSenseUniversityOnClick={handleSenseUniversityOnClick}
            t={t}
            // @ts-expect-error - TS2322 - Type 'number | boolean' is not assignable to type 'boolean'.
            languageSupportEnabled={languageSupportEnabled}
          />

          {authed &&
            !activeEventProcessor &&
            slug !== 'demo' &&
            process.env.NODE_ENV ===
              'production' /* aka "is production or staging" */ && (
              <Notification>
                <strong>Your account is in setup mode,</strong>
                <span>
                  so no automated messages will be sent. To enable your account
                  and go live, please contact Sense.
                </span>
              </Notification>
            )}
        </>
      )}
      <BadBrowserNotification />
      <RouterErrorBoundary>{children}</RouterErrorBoundary>
      {!fullyHidden && (
        <div className={css.walkmeContainer}>
          <WalkmeButton className={css.walkme} />
        </div>
      )}
    </div>
  );
}

export default flow(
  withWindowWidth,
  // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type '(arg1: T & { store: IndexStore; params: { [key: string]: string; }; }) => unknown'.
  fluxify({}, {pure: true}),
  connect(mapStateToProps, mapDispatchToProps),
  provideHooks(dependencies),
)(TopNavigation) as React.ComponentType<OuterProps>;

type TopNavBarProps = {
  store: IndexStore;
  location: Record<any, any>;
  authed: boolean;
  name: string | null | undefined;
  onNameClick?: (e: React.SyntheticEvent) => void;
  pinned?: boolean; // at some point all pages will be pinned;
  logo?: string;
  showMenu?: boolean;
  groupName: string | null | undefined;
  authedUserPhone: string | null | undefined;
  currentPhoneCountry: string;
  totalUnreadCount: number;
  canViewChat: boolean;
  messageRouteMatches: boolean;
  showMessagingInboxDrawer: boolean;
  setIsAiPanelOpen: (value: boolean) => void;
  showAIPanel: boolean;
  landingPageView: boolean;
  trmEnabled: boolean;
  hasSenseAIPermission: boolean;
  userLimit: number;
  handleSenseUniversityOnClick: () => void;
  urlSuffix: string;
  t: (key: string, value: string) => string;
  languageSupportEnabled: boolean;
};

type IframePayload = {
  phoneNumbers?: string[];
  type?: null;
};

type MessagingDetails = {
  type?: 'MULTIPLE_RECIPIENT' | 'CLICKED_NUMBER';
  payload?: IframePayload;
};

type State = {
  showMessagingFrame: boolean;
  messagingDetails?: MessagingDetails;
  showUserLimitModal: boolean;
};
class TopNavBar extends React.PureComponent<TopNavBarProps, State> {
  senseMessagingRef: {
    current: null | HTMLIFrameElement;
  };

  constructor(props: any) {
    super(props);
    this.state = {
      showMessagingFrame: false,
      showUserLimitModal: false,
    };
    this.senseMessagingRef = React.createRef();
  }

  handleOpenMessagingPanel = (data: any) => {
    const {detail} = data;
    if (
      detail?.type === 'MULTIPLE_RECIPIENT' &&
      detail?.payload?.phoneNumbers?.length > this.props.userLimit
    ) {
      this.setState({showUserLimitModal: true});
    } else {
      this.setState({showMessagingFrame: true, messagingDetails: detail});
    }
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<TopNavBarProps, State, any>'.
  componentDidMount() {
    window.addEventListener(
      'openMessagingPanel',
      this.handleOpenMessagingPanel,
    );
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<TopNavBarProps, State, any>'.
  componentWillUnmount() {
    window.removeEventListener(
      'openMessagingPanel',
      this.handleOpenMessagingPanel,
    );
  }

  handleIframeLoad = () => {
    if (this.senseMessagingRef.current !== null) {
      // @ts-expect-error - TS2531 - Object is possibly 'null'.
      this.senseMessagingRef.current.contentWindow.postMessage(
        this.state.messagingDetails,
        window.origin,
      );
    }
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<TopNavBarProps, State, any>'.
  render(): React.ReactNode {
    const {
      store,
      location,
      pinned,
      authed,
      onNameClick,
      name,
      showMenu,
      logo,
      groupName,
      authedUserPhone,
      totalUnreadCount,
      canViewChat,
      messageRouteMatches,
      showMessagingInboxDrawer,
      setIsAiPanelOpen,
      showAIPanel,
      landingPageView,
      trmEnabled,
      hasSenseAIPermission,
      handleSenseUniversityOnClick,
      urlSuffix,
      t,
      languageSupportEnabled,
    } = this.props;

    const {showMessagingFrame} = this.state;

    let senseAiPanelUrl = window.location.origin;
    if (urlSuffix) {
      senseAiPanelUrl = addQueryParamsToUrl(
        `${senseAiPanelUrl}/sense-ai-panel${urlSuffix}`,
        {
          context: 'sense-ai',
        },
      );
    } else {
      senseAiPanelUrl += `/sense-ai-panel?context=sense-ai`;
    }

    const showMessagingDrawerIcon =
      canViewChat &&
      showMessagingInboxDrawer &&
      !messageRouteMatches &&
      (landingPageView || trmEnabled);

    return (
      <div className={css.navPlaceholder}>
        <nav className={pinned ? css.navFixed : css.nav}>
          <Link to="/" className={css.logo}>
            {logo ? (
              <img className={css.logoImage} src={logo} />
            ) : (
              <Logo className={css.logoFallback} />
            )}
          </Link>
          {authed && (
            <>
              <div className={css.authed}>
                <div
                  className={css.chatButtonContainer}
                  // style={{display: 'flex', alignItems: 'center'}}
                >
                  {hasSenseAIPermission && (
                    <div className={css.senseAIPanelBtn}>
                      {languageSupportEnabled && <LanguageSupport />}
                      {/* <Button
                        iconLeftName="sparkles"
                        onClick={() => {
                          setIsAiPanelOpen(true);
                        }}
                        size="small"
                        type={'tertiary'}
                        iconLeftType="solid"
                      >
                        {t('AI_Copilot', 'AI Copilot')}
                      </Button> */}
                      {showAIPanel && (
                        <Panel
                          anchor="right"
                          initialFocus={2}
                          size="large"
                          tapOutsideToClose
                          onClose={() => {
                            setIsAiPanelOpen(false);
                          }}
                          isOpen={showAIPanel}
                        >
                          <iframe
                            width={'100%'}
                            src={senseAiPanelUrl}
                            title={t('AI_Copilot', 'AI Copilot')}
                            style={{border: 0, height: '100vh'}}
                          />
                        </Panel>
                      )}
                    </div>
                  )}

                  {showMessagingDrawerIcon && (
                    <div style={{paddingRight: '16px', display: 'flex'}}>
                      <Button
                        iconLeftName="inbox"
                        onClick={() =>
                          this.setState((prevState) => ({
                            showMessagingFrame: !prevState.showMessagingFrame,
                          }))
                        }
                        size="small"
                        type={'tertiary'}
                        iconLeftType="solid"
                      >
                        {t('Inbox', 'Inbox')}
                      </Button>

                      {totalUnreadCount > 0 ? (
                        <Badge
                          fill={'blue'}
                          text={`${totalUnreadCount}`}
                          classNames={{wrapper: css.messageCountBadge}}
                        />
                      ) : null}
                      <Panel
                        anchor="right"
                        initialFocus={2}
                        onClose={() =>
                          this.setState({
                            showMessagingFrame: false,
                            // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'MessagingDetails | undefined'.
                            messagingDetails: null,
                          })
                        }
                        size="medium"
                        tapOutsideToClose
                        isOpen={showMessagingFrame}
                      >
                        <PanelBody className={css.panelPaddingNull}>
                          <iframe
                            width={'100%'}
                            src={`${window.location.origin}/messages?context=trm-iframe`}
                            title="messaging"
                            style={{border: 0, height: '100vh'}}
                            ref={this.senseMessagingRef}
                            onLoad={this.handleIframeLoad}
                          />
                        </PanelBody>
                      </Panel>
                    </div>
                  )}
                  <Modal
                    isOpen={this.state.showUserLimitModal}
                    onClose={() => this.setState({showUserLimitModal: false})}
                  >
                    <ModalBody>
                      <BulkMessageWrarning
                        userLimit={this.props.userLimit}
                        onClose={() =>
                          this.setState({showUserLimitModal: false})
                        }
                      />
                    </ModalBody>
                  </Modal>
                  {/* @ts-expect-error - TS2741 - Property 'menuContent' is missing in type '{ children: Element[]; className: string; anchor: Element; }' but required in type 'ContextMenuConfig'. */}
                  <ContextMenu
                    className={css.userMenu}
                    anchor={
                      <span data-it-username className={css.userName}>
                        {name}
                      </span>
                    }
                  >
                    <Link
                      className={css.signout}
                      onClick={handleSenseUniversityOnClick}
                    >
                      Sense Learning Center
                    </Link>
                    <Link
                      className={css.signout}
                      // @ts-expect-error - TS2322 - Type '{ children: string; className: string; href: string; target: string; }' is not assignable to type 'IntrinsicAttributes & { to?: LocationType | ((arg1: RerouterLocation) => LocationType) | null | undefined; ... 6 more ...; handleExtension?: boolean | undefined; }'.
                      href="https://form.asana.com/?k=HHJwBxMRKYi4zwNiU_qYVg&d=713022220213973"
                      target="_blank"
                    >
                      Feature Request
                    </Link>
                    <Link
                      className={css.signout}
                      // @ts-expect-error - TS2322 - Type '{ children: string; className: string; href: string; target: string; }' is not assignable to type 'IntrinsicAttributes & { to?: LocationType | ((arg1: RerouterLocation) => LocationType) | null | undefined; ... 6 more ...; handleExtension?: boolean | undefined; }'.
                      href="https://support.sensehq.co/hc/en-us/requests/new"
                      target="_blank"
                    >
                      File an Issue
                    </Link>
                    <Link
                      className={css.signout}
                      to="/reset"
                      onClick={() =>
                        store.me.setState({
                          // @ts-expect-error - TS2345 - Argument of type 'Record<any, any>' is not assignable to parameter of type 'RerouterLocation'.
                          postAuthPath: pathFromLocation(location),
                        })
                      }
                    >
                      Change password
                    </Link>
                    <a
                      className={css.signout}
                      href="https://support.sensehq.co"
                    >
                      Support
                    </a>
                    <Link className={css.signout} to="/signout">
                      Sign out
                    </Link>
                  </ContextMenu>
                </div>
              </div>
            </>
          )}
        </nav>
      </div>
    );
  }
}

const GroupLogo = ({groupName}: {groupName: string}) => (
  <span className={css.groupName}>
    {groupName}
    <PlacementsIcon className={css.groupIcon} />
  </span>
);
